import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Image from "components/image";
import RHFInput from "components/form/rhf-input";
import useProfilePicture from "hooks/use-profile-picture";
import { userSelector } from "store/user";
import { userPictureUrl } from "utils/consts";

// Autofill detail tokens for editing your own profile; someone else's
// record keeps every field "off" so the browser never offers your own
// details for theirs. alt_email is absent on purpose: the account email
// autofilled into an alias field would only duplicate it.
const SELF_AUTOFILL_TOKENS = {
    name: "name",
    phone: "tel",
    address_1: "address-line1",
    address_2: "address-line2",
    postcode: "postal-code",
    country: "country-name",
    job_role: "organization-title",
};

// Kept from the pre-#1128 dialog verbatim: the important-flagged
// overrides hold the black input/label text against Material Tailwind's
// own color utilities, and max-w-full truncate is the over-long-label
// escape rhf-input's defaults exist to provide.
const inputIconStyle = {
    className: "!text-black placeholder:!text-black",
    labelProps: {
        className:
            "!text-black peer-placeholder-shown:!text-black peer-focus:!text-black max-w-full truncate",
    },
};

function buildUserValues(userData) {
    return {
        name: userData?.name || "",
        alt_email: userData?.alt_email || "",
        phone: userData?.phone || "",
        address_1: userData?.address_1 || "",
        address_2: userData?.address_2 || "",
        postcode: userData?.postcode || "",
        country: userData?.country || "",
        job_role: userData?.job_role || "",
    };
}

// Lives inside the <Dialog> so every open remounts the form with fresh
// defaults from the current userData prop (recipe P5). userData can
// change identity mid-open (Redux/SWR-held userData on /account,
// editingUser state on /organisation; stable per record), so the reset
// effect keys on that prop to re-baseline the form.
//
// Picture operations are deferred to Save (issue #1128): picking a file
// or marking a delete only arms pending state in the picture hook;
// Save runs the pending operation before the text-field submit, and
// closing the dialog discards it.
function ManageUserForm({
    userData,
    isSelf,
    handleUserSubmit,
    validationSchemaUser,
    userId,
    UploadPicture,
    handleClose,
    isRunningRef,
}) {
    const autoCompleteFor = (field) =>
        (isSelf && SELF_AUTOFILL_TOKENS[field]) || "off";

    const methods = useForm({
        mode: "onChange",
        defaultValues: buildUserValues(userData),
        resolver: yupResolver(validationSchemaUser),
    });
    const { reset, handleSubmit } = methods;
    // Read both flags up front: formState is a subscription proxy, so a
    // conditional read (`!isDirty || !isValid`) short-circuits before
    // isValid while isDirty is false. RHF then never subscribes to
    // validity updates and isValid stays frozen at false.
    const { isValid, isDirty } = methods.formState;

    const { pictureVersion } = useSelector(userSelector);
    const {
        previewSrc,
        pendingDelete,
        hasPendingOps,
        isRunning,
        pickFile,
        markDelete,
        runPendingOps,
        discard,
    // Mirror the running flag for the outer dialog's Escape/backdrop
    // handler, which otherwise has no access to the hook's state.
    } = useProfilePicture({ userId });
    isRunningRef.current = isRunning;

    // True until the picture URL fails to load: drives the Delete
    // button's presence and the placeholder fallback.
    const [pictureAvailable, setPictureAvailable] = useState(true);

    const inputLogoFileRef = useRef(null);

    useEffect(() => {
        reset(buildUserValues(userData), { keepErrors: true });
    }, [userData, reset]);

    const imageSrc = previewSrc
        ?? (pendingDelete || !pictureAvailable
            ? UploadPicture
            : userPictureUrl(userId, pictureVersion));

    // Save runs picture operations first; a picture failure aborts the
    // save (the hook already toasted) and leaves the pending op armed
    // for a retry. Pending state is deliberately retained on failure:
    // the server replaces/removes the file, so a retry is idempotent.
    const onSubmit = handleSubmit(async (values) => {
        try {
            const ran = await runPendingOps();
            if (!ran) {
                // A re-entry while an operation is already in flight:
                // the first submit completes the save.
                return;
            }
        } catch {
            return;
        }
        await handleUserSubmit(values);
    });

    const closeAndDiscard = () => {
        discard();
        handleClose();
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <DialogBody className="dialog__body ">
                    <div>
                        <div
                            className="md:flex md:flex-col md:items-center
                                    rounded-2xl p-4 mb-4"
                        >
                            <div className="">
                                <Image
                                    alt="Profile picture"
                                    src={imageSrc}
                                    className="rounded-xl"
                                    onError={({ currentTarget }) => {
                                        // Prevents looping back to the
                                        // placeholder on later errors.
                                        currentTarget.onerror = null;
                                        if (!previewSrc && !pendingDelete) {
                                            setPictureAvailable(false);
                                        }
                                    }}
                                />
                            </div>
                            <div className="p-2">
                                <Button
                                    variant="filled"
                                    type="button"
                                    disabled={isRunning}
                                    onClick={() =>
                                        inputLogoFileRef.current?.click()
                                    }
                                    className="btn btn--secondary"
                                >
                                    Upload Profile Image
                                </Button>
                                {pictureAvailable && !previewSrc && !pendingDelete && (
                                    <Button
                                        variant="filled"
                                        type="button"
                                        disabled={isRunning}
                                        onClick={() => markDelete()}
                                        className="btn btn--danger"
                                        size="lg"
                                    >
                                        Delete Profile Image
                                    </Button>
                                )}
                                <input
                                    onChange={(event) => {
                                        pickFile(event.target.files?.[0]);
                                        // Re-arming the same file through
                                        // the input needs a change event
                                        // next time.
                                        event.target.value = "";
                                    }}
                                    ref={inputLogoFileRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="name"
                            type="text"
                            color="gray"
                            label="Display Name"
                            iconName="person"
                            autoComplete={autoCompleteFor("name")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="alt_email"
                            type="email"
                            color="gray"
                            label="Alternative Email Address"
                            iconName="alternate_email"
                            autoComplete={autoCompleteFor("alt_email")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="phone"
                            type="text"
                            color="gray"
                            label="Contact Phone Number"
                            iconName="call"
                            autoComplete={autoCompleteFor("phone")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="address_1"
                            type="text"
                            color="gray"
                            label="First Line of Address"
                            iconName="contact_mail"
                            autoComplete={autoCompleteFor("address_1")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="address_2"
                            type="text"
                            color="gray"
                            label="Second Line of Address"
                            iconName="contact_mail"
                            autoComplete={autoCompleteFor("address_2")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="postcode"
                            type="text"
                            color="gray"
                            label="Postcode/Zip Code"
                            iconName="contact_mail"
                            autoComplete={autoCompleteFor("postcode")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="country"
                            type="text"
                            color="gray"
                            label="Country"
                            iconName="contact_mail"
                            autoComplete={autoCompleteFor("country")}
                            {...inputIconStyle}
                        />
                    </div>
                    <div className="p-2 font-body  p-4 rounded-xl mt-2">
                        <RHFInput
                            name="job_role"
                            type="text"
                            color="gray"
                            label="Role in Organisation"
                            iconName="badge"
                            autoComplete={autoCompleteFor("job_role")}
                            {...inputIconStyle}
                        />
                    </div>
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        className="btn btn--secondary"
                        variant="filled"
                        // Cancel while an upload/delete is mid-flight
                        // would still let the submit continuation run
                        // the text PUT afterwards (review finding).
                        disabled={isRunning}
                        onClick={closeAndDiscard}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        // Image-only changes count toward Save (issue
                        // #1128). isValid still gates: a pristine-invalid
                        // form (e.g. a legacy empty required name) keeps
                        // the image-only save blocked -- deliberate.
                        disabled={isRunning || ((!isDirty && !hasPendingOps) || !isValid)}
                        className="btn btn--primary ml-2"
                        variant="filled"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </form>
        </FormProvider>
    );
}

export default function ManageUserDialog({
    userData,
    isSelf = false,
    handleUserSubmit,
    showManageUserDialog,
    setShowManageUserDialog,
    validationSchemaUser,
    userId,
    UploadPicture,
}) {
    const isRunningRef = useRef(false);

    const handleClose = () => {
        // Same guard as Cancel: Escape and backdrop clicks arrive here
        // while a picture operation is running, and closing mid-flight
        // still lets the submit continuation run the text PUT.
        if (!isRunningRef.current) {
            setShowManageUserDialog((showManageUserDialog) => !showManageUserDialog);
        }
    };

    return (
        <Dialog
            open={showManageUserDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">Manage User</DialogHeader>
            <ManageUserForm
                userData={userData}
                isSelf={isSelf}
                handleUserSubmit={handleUserSubmit}
                validationSchemaUser={validationSchemaUser}
                userId={userId}
                UploadPicture={UploadPicture}
                handleClose={handleClose}
                isRunningRef={isRunningRef}
            />
        </Dialog>
    );
}
